import React, { FC, KeyboardEvent, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
    DropdownContent,
    DropdownDetachedTop,
    DropdownDivider,
    DropdownItemBody,
    DropdownItemCaption,
    DropdownItemIcon,
    DropdownItemText,
    DropdownList,
    DropdownListItem,
    DropdownToneContext
} from './dropdown';
import { DropdownPlacement, useDropdown } from '../hooks/useDropdown';

type DropdownActionsOrientation = 'LEFT' | 'RIGHT';
type DropdownActionsVerticalOrientation = 'TOP' | 'BOTTOM';

interface DropdownActionsProps {
    header: string | ReactNode | ((state: { isOpen: boolean }) => ReactNode);
    detachedTop?: ReactNode;
    sections?: ReactNode[][];
    items?: ReactNode[];
    orientation?: DropdownActionsOrientation;
    verticalOrientation?: DropdownActionsVerticalOrientation;
    placement?: DropdownPlacement;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    maxHeight?: string;
    surfacePadding?: string;
    zIndex?: number;
    matchTriggerWidth?: boolean;
    overlay?: boolean;
    overlayBackground?: string;
}

interface DropdownActionItemProps {
    children: ReactNode;
    caption?: ReactNode;
    icon?: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    selected?: boolean;
    danger?: boolean;
    testId?: string;
}

const DropdownActionsContainer = styled.div`
    display: inline-flex;
    align-items: center;
`;

const DropdownActionsTrigger = styled.div`
    display: inline-flex;
    align-items: center;
    user-select: none;
`;

const resolvePlacement = (
    placement?: DropdownPlacement,
    orientation?: DropdownActionsOrientation,
    verticalOrientation?: DropdownActionsVerticalOrientation
): DropdownPlacement => {
    if (placement) return placement;

    if (verticalOrientation === 'BOTTOM') {
        return orientation === 'RIGHT' ? 'top-end' : 'top-start';
    }

    return orientation === 'RIGHT' ? 'bottom-end' : 'bottom-start';
};

const MENU_CONTROLS =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const normalizeSections = (sections?: ReactNode[][], items?: ReactNode[]) => {
    const rawSections = sections ?? (items ? [items] : []);
    return rawSections.map((section) => section.filter(Boolean)).filter((section) => section.length > 0);
};

// Use DropdownActionItem for clickable entries inside DropdownActions.
// Set danger for destructive actions, selected for the active item, and disabled when the action is temporarily unavailable.
export const DropdownActionItem: FC<DropdownActionItemProps> = ({ children, caption, icon, onClick, disabled, selected, danger, testId }) => {
    const soft = useContext(DropdownToneContext) === 'soft';
    return (
        <DropdownListItem
            type='button'
            $active={selected}
            $danger={danger}
            $soft={soft}
            role='menuitem'
            disabled={disabled}
            onClick={() => !disabled && onClick?.()}
            data-testid={testId}
        >
            {icon && <DropdownItemIcon>{icon}</DropdownItemIcon>}
            <DropdownItemBody>
                <DropdownItemText>{children}</DropdownItemText>
                {caption && <DropdownItemCaption>{caption}</DropdownItemCaption>}
            </DropdownItemBody>
        </DropdownListItem>
    );
};

// Use DropdownActions for row or toolbar action menus, including three-dots menus.
// Example:
// <DropdownActions
//     header={<BasicButton type='button' $size='small' $type='icon' aria-label={T._('Actions', 'Admin')}><Icon><VerticalThreePoint /></Icon></BasicButton>}
//     items={[<DropdownActionItem key='edit' onClick={handleEdit}>{T._('Edit', 'Admin')}</DropdownActionItem>]}
//     orientation='RIGHT'
// />
// Use sections instead of items when groups need dividers; use placement only when LEFT/RIGHT and TOP/BOTTOM are not specific enough.
// Use detachedTop for a primary action that must sit in its own card above the dropdown panel.
export const DropdownActions: FC<DropdownActionsProps> = ({
    header,
    detachedTop,
    sections,
    items,
    orientation,
    verticalOrientation,
    placement,
    width,
    minWidth = '300px',
    maxWidth,
    maxHeight,
    surfacePadding = '0',
    zIndex,
    matchTriggerWidth,
    overlay,
    overlayBackground
}) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const visibleSections = useMemo(() => normalizeSections(sections, items), [items, sections]);

    // A MENU, FOR ASSISTIVE TECHNOLOGY TOO (accessibility, 2026-10-22): the trigger says it opens a menu and whether it
    // is open; inside, arrows move between the items and Tab leaves the menu, as in the WAI-ARIA menu pattern. The
    // trigger is whatever `header` renders, so the attributes go on its first button.
    const triggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const button = triggerRef.current?.querySelector('button') ?? triggerRef.current;
        button?.setAttribute('aria-haspopup', 'menu');
        button?.setAttribute('aria-expanded', String(isOpen));
    }, [isOpen]);

    // Items can be custom content (the AI Studio prompt history puts plain buttons next to its "Clear all"): the arrows walk
    // every control of the menu, and Tab leaves it as in the WAI-ARIA pattern only when the menu is made of items alone,
    // otherwise Tab keeps walking its controls.
    const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const menuItems = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(MENU_CONTROLS));
        // Inside a field the arrows, Home and End move the caret.
        if (!menuItems.length || (event.target as HTMLElement).matches('input, textarea, select, [contenteditable="true"]')) return;
        const index = menuItems.indexOf(document.activeElement as HTMLElement);
        const go = (item?: HTMLElement) => {
            if (!item) return;
            event.preventDefault();
            item.focus();
        };
        switch (event.key) {
            case 'ArrowDown':
                return go(menuItems[(index + 1) % menuItems.length]);
            case 'ArrowUp':
                return go(menuItems[(index - 1 + menuItems.length) % menuItems.length]);
            case 'Home':
                return go(menuItems[0]);
            case 'End':
                return go(menuItems[menuItems.length - 1]);
            case 'Tab':
                if (menuItems.some((item) => item.getAttribute('role') !== 'menuitem')) return;
                // The focus goes back to the trigger first, so Tab carries on from there.
                (triggerRef.current?.querySelector('button') ?? triggerRef.current)?.focus();
                closeDropdown();
        }
    };

    return (
        <DropdownActionsContainer>
            <DropdownActionsTrigger
                ref={triggerRef}
                data-open={isOpen || undefined}
                onClick={(e) => {
                    e.stopPropagation();
                    isOpen ? closeDropdown() : openDropdown(e.currentTarget, resolvePlacement(placement, orientation, verticalOrientation));
                }}
            >
                {typeof header === 'function' ? header({ isOpen }) : header}
            </DropdownActionsTrigger>
            {isOpen && visibleSections.length > 0 && (
                <Dropdown
                    width={width}
                    minWidth={minWidth}
                    maxWidth={maxWidth}
                    maxHeight={maxHeight}
                    surfacePadding={surfacePadding}
                    zIndex={zIndex}
                    matchTriggerWidth={matchTriggerWidth}
                    overlay={overlay}
                    overlayBackground={overlayBackground}
                    detachedTop={
                        detachedTop && (
                            <DropdownDetachedTop
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeDropdown();
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {detachedTop}
                            </DropdownDetachedTop>
                        )
                    }
                >
                    <DropdownContent
                        onClick={(e) => {
                            e.stopPropagation();
                            closeDropdown();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={onMenuKeyDown}
                    >
                        <DropdownList role='menu' data-dropdown-actions>
                            {visibleSections.map((section, sectionIndex) => (
                                <React.Fragment key={`section_${sectionIndex}`}>
                                    {sectionIndex > 0 && <DropdownDivider role='separator' />}
                                    {section.map((item, itemIndex) => (
                                        <React.Fragment key={`item_${sectionIndex}_${itemIndex}`}>{item}</React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </DropdownList>
                    </DropdownContent>
                </Dropdown>
            )}
        </DropdownActionsContainer>
    );
};
